import { Alert, Avatar, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { createTheme } from '@mui/material/styles';
import { useEffect, useState } from "react";
import LogService from "../../services/LogService";
import IHoliday from "../../types/Holiday";
import ILog from "../../types/Log";
import ConfigUtils from "../../utils/Config";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import * as constantes from "../../common/Constantes";
import IAlert from "../../types/Alert";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { aFecha, ValorFecha } from '../../utils/FechaSelector';
import Utilities from "../../common/Utilities";
import ConfigService from "../../services/ConfigService";
import { useNavigate } from "react-router-dom";
import AgendaUtils from "../../utils/Agenda";

const locale = constantes.locale;

const Holidays = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const [loading, setLoading] = useState(false);
    const [modalNewFestivo, setModalNewFestivo] = useState(false);
    const [defaultHolidays, setDefaultHolidays] = useState<Array<IHoliday>>(ConfigUtils.getHolidays());
    const [holidays, setHolidays] = useState<Array<IHoliday>>(ConfigUtils.getHolidays());
    const [clinicProvincia] = useState<string>(ConfigUtils.getProvincia());

    const [selectedFestivo, setSelectedFestivo] = useState<IHoliday>();
    const [modalDelete, setModalDelete] = useState(false);

    // Fecha del festivo tal cual la da el selector (ver utils/FechaSelector); errorFecha: aviso tras intentar guardar
    const [start, setStart] = useState<ValorFecha>(new Date(new Date().setHours(0, 0, 0, 0)));
    const [errorFecha, setErrorFecha] = useState(false);
    const [name, setName] = useState<string>('');
    const [errorName, setErrorName] = useState(false);

    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;

    const [collapsedYears, setCollapsedYears] = useState<Record<number, boolean>>({
      [currentYear]: false,
      [nextYear]: true,
    });

    useEffect(() => {
        return () => {
            // Executed only when component unmounts,
            let e = new Event("componentUnmount");
            document.dispatchEvent(e);
        }
    }, []);

    useEffect(() => {
        function doOnUnmount() {
            if (holidays.toString() !== defaultHolidays.toString()) {
                // Perform an Action only if something changed
                if (window.confirm('Hay cambios sin guardar, ¿Quiere guardar los cambios de los festivos antes de continuar?')) {
                    updateFestivos();
                }
            }
        }

        document.addEventListener("componentUnmount", doOnUnmount);
        return () => {
            // This is done whenever value of somethingChanged changes
            document.removeEventListener("componentUnmount", doOnUnmount);
        }

    }, [holidays, defaultHolidays]);

    useEffect(() => {
        setHolidays(prev => {
            const filled = AgendaUtils.fillMissingYearsHolidays(prev, clinicProvincia);
            if (filled === prev) return prev;
            setDefaultHolidays(filled);
            return filled;
        });
    }, [clinicProvincia]);

    const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const closeModalFestivo = () => {
        setStart(new Date(new Date().setHours(0, 0, 0, 0)));
        setName('');
        setErrorName(false);
        setErrorFecha(false);
        setModalNewFestivo(false);
    };

    const deleteFestivo = () => {
        setHolidays(holidays.filter(h => h.name !== selectedFestivo?.name));
        setSelectedFestivo(undefined);
        setModalDelete(false);
    };

    const addFestivo = () => {
        // Fecha vacía, a medio escribir o fuera de los años que admite el selector: no se añade (llegaba a la API sin fecha y,
        // como el servidor borra y recrea todos los festivos, el resto se perdía)
        const fecha = aFecha(start);
        const fechaValida = !!fecha && fecha.getFullYear() >= currentYear && fecha.getFullYear() <= nextYear;
        setErrorFecha(!fechaValida);
        const nombreVacio = !!Utilities.isNullEmptyUndefined(name);
        setErrorName(nombreVacio);
        if (nombreVacio || !fecha || !fechaValida) return;
        fecha.setHours(0, 0, 0, 0);

        const exists = holidays.filter(h => new Date(h.start).getTime() === fecha.getTime());
        if (exists.length !== 0) {
            setAlert({ open: true, severity: 'warning', msg: 'Ya existe un festivo ese día' });
            return;
        }

        const endFestivo = new Date(fecha);
        endFestivo.setDate(endFestivo.getDate() + 1);

        const updatedHolidays = [...holidays, { start: fecha, end: endFestivo, name, userId: ConfigUtils.getUserId() }];
        updatedHolidays.sort((a: IHoliday, b: IHoliday) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
        });
        setHolidays(updatedHolidays);
        closeModalFestivo();
    };

    const updateFestivos = () => {
        setLoading(true);

        ConfigService.updateFestivos(ConfigUtils.getUserId(), holidays)
            .then((response: any) => {
                ConfigUtils.editFestivos(holidays);
                setDefaultHolidays(holidays);
                setAlert({ open: true, severity: 'success', msg: response.data.message });
            })
            .catch((e: any) => {
                if (e.response.status === 401) {
                    localStorage.clear();
                    history(constantes.R_HOME);
                } else {
                    const error: ILog = { method: 'Holidays.Component.updateFestivos', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                    setAlert({ open: true, severity: 'error', msg: e.response.data.message });
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const toggleYear = (year: number) => {
      setCollapsedYears(prev => ({
        ...prev,
        [year]: !prev[year],
      }));
    };

    try {

        const holidaysByYear = [...holidays]
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
            .reduce<Record<number, IHoliday[]>>((acc, holiday) => {
              const year = new Date(holiday.start).getFullYear();
              if (!acc[year]) acc[year] = [];
              acc[year].push(holiday);
              return acc;
            }, {});

        return (
            <Grid>
                <Grid container sx={{ maxWidth: { xs: '100%', sm: '600px', md: '800px' } }}>

                    {holidays.toString() !== defaultHolidays.toString() &&
                        <Alert severity="warning" sx={{ mb: theme.spacing(1) }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ mr: theme.spacing(1) }}>Hay cambios sin guardar</Typography>
                                <Button onClick={updateFestivos} color="success" variant="contained" >
                                    <Typography variant="body1" sx={{ fontSize: '15px', fontWeight: 'bold', color: 'white' }} >Guardar cambios</Typography>
                                </Button>
                            </div>
                        </Alert>
                    }

                    {loading ?
                        <Grid container>
                            <CircularProgress style={{ margin: '20px auto' }} />
                        </Grid> :

                        <Grid item md={12}>
                            <Grid>
                                <Grid container spacing={2}>
                                    <Grid item md={4}>
                                        <Button onClick={() => setModalNewFestivo(true)} color="success" variant="contained" >
                                            <Typography variant="body1" sx={{ fontSize: '15px', fontWeight: 'bold', color: 'white' }}>Añadir festivo</Typography>
                                        </Button>
                                    </Grid>
                                </Grid>
                                <List dense>
                                  {Object.entries(holidaysByYear).map(([yearStr, yearHolidays]) => {
                                    const year = Number(yearStr);
                                    const isCollapsed = collapsedYears[year] ?? true;
                                
                                    return (
                                      <div key={year}>
                                        <Typography
                                          variant="h6"
                                          onClick={() => toggleYear(year)}
                                          sx={{
                                            mt: 3,
                                            mb: 1,
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            }
                                          }}
                                        >
                                          {year}
                                        </Typography>
                                      
                                        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                                          {yearHolidays.map(holiday => {
                                            const date = new Date(holiday.start);
                                        
                                            return (
                                              <ListItem
                                                key={holiday._id || `${holiday.name}-${holiday.start}`}
                                                divider
                                              >
                                                <ListItemText
                                                  primary={holiday.name}
                                                  secondary={`${date.getDate()} de ${
                                                    constantes.monthNames[date.getMonth()]
                                                  }`}
                                                />
                                                <ListItemSecondaryAction>
                                                  <Tooltip title="ELIMINAR FESTIVO">
                                                    <IconButton
                                                      edge="end"
                                                      onClick={() => {
                                                        setModalDelete(true);
                                                        setSelectedFestivo(holiday);
                                                      }}
                                                    >
                                                      <DeleteIcon />
                                                    </IconButton>
                                                  </Tooltip>
                                                </ListItemSecondaryAction>
                                              </ListItem>
                                            );
                                          })}
                                        </Collapse>
                                      </div>
                                    );
                                  })}
                                </List>
                            </Grid>
                        </Grid>}
                </Grid>

                {/* CONFIRMACIÓN BORRAR */}
                <Dialog open={modalDelete} maxWidth="lg" onClose={() => setModalDelete(false)}>
                    <DialogTitle>ELIMINAR FESTIVO</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{`¿Está seguro de que desea eliminar el festivo ${selectedFestivo?.name}?`}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={deleteFestivo} color="primary">SI</Button>
                        <Button onClick={() => setModalDelete(false)} color="primary" autoFocus>NO</Button>
                    </DialogActions>

                </Dialog>

                {/* AÑADIR FESTIVO */}
                <Dialog open={modalNewFestivo} maxWidth="lg" fullWidth onClose={closeModalFestivo}>
                    <DialogContent>
                        <Typography variant="h4" sx={{ display: 'inline-flex' }}>
                            <Avatar sx={{ margin: theme.spacing(1), backgroundColor: '#2e7d32' }}><AddIcon /></Avatar><div style={{ margin: '10px auto' }}>Nuevo festivo</div>
                        </Typography>

                        <Grid container spacing={1}>
                            <Grid item md={4} xs={12}>
                                <TextField value={name} onChange={(e) => setName(e.target.value)} variant="outlined" label="Nombre del festivo" margin="normal" autoFocus required fullWidth error={errorName} helperText={errorName ? 'El nombre es obligatorio' : ''} />
                            </Grid>
                            <Grid item md={4} xs={12} sx={{ mt: theme.spacing(2) }}>
                                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                                    <DatePicker
                                        renderInput={(props: any) => <TextField required fullWidth {...props} error={errorFecha || props.error} helperText={errorFecha ? `Indica una fecha válida entre ${currentYear} y ${nextYear}` : undefined} />}
                                        label="Fecha"
                                        toolbarFormat="dd-mm-yyyy"
                                        value={start}
                                        onChange={(date: Moment | null) => { setStart(date); setErrorFecha(false); }}
                                        minDate={moment(new Date(currentYear, 0, 1))}
                                        maxDate={moment(new Date(nextYear, 11, 31))}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={addFestivo}>Guardar</Button>
                        <Button onClick={closeModalFestivo}>Cancelar</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                    <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
                </Snackbar>
            </Grid>
        );
    } catch (e: any) {
        const error: ILog = { method: 'Holidays.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }
};

export default Holidays;